import ModalHeader from "./ModalHeader";
import ModalFormWrapper from "./ModalFormWrapper";
import {
  useForm,
  SubmitHandler,
  useWatch,
} from "react-hook-form";
import { CalculatorPrototypeDrive, FormTypes } from "../../types/FormTypes";
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { debounce } from "lodash";
import { formOrder, mapFormData, reverseMapFormData } from "../../utils/utils";

interface IProjectModal {
  toggleModal: boolean;
  setToggleModal: Function;
}

function ProjectModal(props: IProjectModal) {
  const { toggleModal, setToggleModal } = props;
  const [step, setStep] = useState<number>(1);
  const [compareItemArray, setCompareItemArray] = useState<Array<any>>([]);
  const [excelDownloadLink, setExcelDownlaodLink] = useState<string>();
  const [selectedItemsToCompare, setSelectedItemsToCompare] = useState<
    Array<any>
  >([]);
  const [dataCheckingMode, setDataCheckingMode] = useState<boolean>(false);
  const [itemToConfigure, setItemToConfigure] = useState<any>({});
  const [externalForceCount, setExternalForceCount] = useState<number>(0);
  const [momentsCount, setMomentsCount] = useState<number>(0);
  const [ existingProjectId, setExistingProjectId ] = useState<string | null>(null);
  const [initialSelectionApplied, setInitialSelectionApplied] = useState(false);
  const {
    register,
    unregister,
    handleSubmit,
    watch,
    setFocus,
    reset,
    getValues,
    formState: { errors },
    control,
    trigger,
    setValue,
  } = useForm<FormTypes>({
    shouldFocusError: false,
    defaultValues: {
      driveSystemBelt: true,
      driveSystemChain: true,
      driveSystemLeadScrew: true,
      driveSystemBallScrew: true,
      orientation: "orientationHorizontal"
    }
  });
  const formData = useWatch({ control });

  const prevFormDataRef = useRef(formData);

  const onSubmit: SubmitHandler<FormTypes> = (data: FormTypes) => {
    let payload: CalculatorPrototypeDrive = mapFormData(data);
    axios
      .post(
        "https://bw-stg-api.clarityclient.com/CalculatorPrototypeDrive",
        payload,
      )
      .then((response) => {
        if (response && response.data) {
          let result = response.data.filter(
            (x: { selectedDrive: boolean; viableDrive: boolean }) =>
              x.selectedDrive === true && x.viableDrive === true
          );
          setCompareItemArray(result);
          if (result.length) {
            setDataCheckingMode(false);
          }
          if (!excelDownloadLink && result.length) {
            const file = result[0].excelFileLink;
            setExcelDownlaodLink(
              ``
            );
          }
          setSelectedItemsToCompare([]);
        } else {
          console.log("There was an error retrieving the data.");
        }
      })
      .catch((e) => {
        setCompareItemArray([]);
        setSelectedItemsToCompare([]);
        console.log(e);
      });
  };

  const debouncedAPICall = useMemo(
    () => debounce(handleSubmit(onSubmit), 1000),
    []
  );

  const runValidation = async () => {
    const isValid = await trigger();
    return isValid;
  }

  // Save the project data and close the modal
  const saveAndClose = async () => {
    const currentFormData = getValues();
    const selectedProductIds = selectedItemsToCompare.map((x) => x.id);
    const payload: CalculatorPrototypeDrive = {
      ...mapFormData(currentFormData),
      currentPage: step,
      selectedProductIds: selectedProductIds,
      externalForceCount: externalForceCount,
      momentsCount: momentsCount,
    }
    const jsonPayload = JSON.stringify(payload);
    console.log(jsonPayload);
    setToggleModal(!toggleModal)
    // If the user is logged out, save project data to SessionStorage and direct to login page
    const params = new URLSearchParams(window.location.search);
    const loggedInParam = params.get("loggedIn");
    if (loggedInParam == "false") {
      sessionStorage.setItem("SavedProjectData", jsonPayload);
      window.location.href = "/customer/account/login/";
      return;
    }
  
    // Prepare the full request payload
    const requestBody = {
      project: {
        id: existingProjectId || null,
        name: payload.projectName,
        content: jsonPayload,
      }
    };
  
    try {
      const response = await fetch("/rest/V1/clarity/mine/project", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify(requestBody),
      });
  
      if (!response.ok) {
        throw new Error("Failed to save project");
      }
  
      const result = await response.json();
      console.log("Project saved:", result);
      window.parent.location.reload();
    } catch (error) {
      console.error("Error saving project:", error);
    }
  };

  const openModalForProject = (projectId: any) => {
    setExistingProjectId(projectId);
    setToggleModal(true);

    if (!projectId) return;
  
    const fetchProject = async () => {
      try {
        const response = await fetch(
          `/rest/V1/clarity/mine/project/${projectId}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest"
            },
          }
        );
  
        if (!response.ok) {
          throw new Error("Failed to fetch project data");
        }
  
        const existingProjectData = await response.json();
        const result: FormTypes = reverseMapFormData(existingProjectData.content);
  
        if (result.externalForceCount) setExternalForceCount(result.externalForceCount);
        if (result.momentsCount) setMomentsCount(result.momentsCount);
        if (result.currentPage) setStep(result.currentPage);
  
        if (result.selectedProductIds && compareItemArray.length > 0) {
          const selectedItems = compareItemArray.filter((item) =>
            result.selectedProductIds!.includes(item.id)
          );
          setSelectedItemsToCompare(selectedItems);
        }
        reset(result);
      } catch (error) {
        console.error("Error fetching project:", error);
      }
    };
  
    fetchProject();
  };

  useEffect(() => {
    const handleMessage = (event: any) => {
      // security check
      // if (event.origin !== window.location.origin) return;

      const { type, projectId } = event.data;
      if (type === "OPEN_PROJECT_MODAL" && projectId) {
        openModalForProject(projectId);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (initialSelectionApplied) return;
    if (compareItemArray.length === 0) return;
    const currentFormData = getValues();
    if (currentFormData.selectedProductIds && Array.isArray(currentFormData.selectedProductIds)) {
      const selectedItems = compareItemArray.filter((item) =>
        currentFormData.selectedProductIds!.includes(item.id)
      );
      setSelectedItemsToCompare(selectedItems);
    }
    setInitialSelectionApplied(true);
  }, [compareItemArray, getValues, initialSelectionApplied]);

  useEffect(() => {
    const prev = prevFormDataRef.current;

    const onlyProjectNameChanged =
      prev &&
      formData?.projectName !== prev?.projectName &&
      JSON.stringify({ ...formData, projectName: undefined }) ===
        JSON.stringify({ ...prev, projectName: undefined });

        prevFormDataRef.current = formData;

    if (onlyProjectNameChanged || step < 1) return;

    const validateAndSubmit = async () => {
      let valid = await runValidation();
      if (!valid) return;
      debouncedAPICall();
    };

    validateAndSubmit();
  }, [formData, debouncedAPICall]);

  useEffect(() => {
    const prev = prevFormDataRef.current;

    const onlyProjectNameChanged =
      prev &&
      formData?.projectName !== prev?.projectName &&
      JSON.stringify({ ...formData, projectName: undefined }) ===
        JSON.stringify({ ...prev, projectName: undefined });

        prevFormDataRef.current = formData;

    if (onlyProjectNameChanged) return;
    
    const validateAndHandleErrors = async () => {
      const isValid = await runValidation();
      if (!isValid) {
        if (compareItemArray.length) {
          setCompareItemArray([]);
        }
        if (selectedItemsToCompare.length) {
          setSelectedItemsToCompare([]);
        }
      }
    };
    validateAndHandleErrors();
  }, [formData, errors, dataCheckingMode]);

  useEffect(() => {
    const form = document.querySelector("form");
    const handleEnterKey = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        focusUser();
        event.preventDefault(); // Prevent form submission on Enter
      }
    };

    form?.addEventListener("keypress", handleEnterKey);

    // Clean up the event listener
    return () => {
      form?.removeEventListener("keypress", handleEnterKey);
    };
  }, [dataCheckingMode]);

  useEffect(() => {
    if (dataCheckingMode) {
      focusUser();
    }
  }, [dataCheckingMode, errors]);

  const focusUser = () => {
    if (dataCheckingMode && Object.keys(errors).length > 0) {
      setTimeout(() => {
        const firstErrorKey: any = formOrder.find(
          // @ts-ignore
          (fieldName) => errors[fieldName]
        );
        // If a field with an error is found, set focus to it
        if (firstErrorKey) {
          setFocus(firstErrorKey);
        }
      }, 500);
    }
  };

  const onBlurHandler = () => {
    focusUser();
  };

  const setDataCheckingModeHandler = () => {
    setDataCheckingMode(true);
    focusUser();
  };

  const handleCheckboxChange = (item: any) => {
    let newSelectedItemsToCompare = [...selectedItemsToCompare];
    const itemIndexInSelected = selectedItemsToCompare.findIndex(
      (x) => x.id === item.id && x.vendorPN === item.vendorPN
    );
    // If the item is already selected, remove it from the selectedItemsToCompare array
    if (itemIndexInSelected > -1) {
      newSelectedItemsToCompare.splice(itemIndexInSelected, 1);
    } else {
      // If the item is not selected, add it
      newSelectedItemsToCompare.push(item);
    }
    // Then sort the updated selectedItemsToCompare array based on the order in compareItemArray
    newSelectedItemsToCompare.sort((a, b) => {
      return (
        compareItemArray.findIndex((x) => x.id === a.id) -
        compareItemArray.findIndex((x) => x.id === b.id)
      );
    });
    setSelectedItemsToCompare(newSelectedItemsToCompare);
  };

  const handleCheckNone = () => {
    setSelectedItemsToCompare([]);
  };

  const nextStep = (): void => {
    setStep(step + 1);
  };

  const setAnyStep = (step: number): void => {
    setStep(step);
  };

  return (
    <div
      className="modal fade in project-modal"
      tabIndex={-1}
      role="dialog"
      style={{ display: "block", overflowY: "scroll" }}
    >
      <div
        className="modal-dialog modal-xxl"
        role="document"
        id="configurator-modal"
      >
        <div className="modal-content">
          <form onSubmit={debouncedAPICall}>
            <div className="modal-header blue-bg justify-content-between d-flex align-items-center">
              <ModalHeader
                step={step}
                setAnyStep={setAnyStep}
                register={register}
                errors={errors}
                watch={watch}
                toggleModal={toggleModal}
                setToggleModal={setToggleModal}
                saveAndClose={saveAndClose}
                projectName={formData.projectName}
              />
            </div>
            <div className="modal-body margin-bottom">
              <ModalFormWrapper
                register={register}
                unregister={unregister}
                errors={errors}
                watch={watch}
                nextStep={nextStep}
                setAnyStep={setAnyStep}
                step={step}
                setValue={setValue}
                compareItemArray={compareItemArray}
                handleCheckboxChange={handleCheckboxChange}
                handleCheckNone={handleCheckNone}
                selectedItemsToCompare={selectedItemsToCompare}
                itemToConfigure={itemToConfigure}
                setItemToConfigure={setItemToConfigure}
                formData={formData}
                setDataCheckingModeHandler={setDataCheckingModeHandler}
                dataCheckingMode={dataCheckingMode}
                trigger={trigger}
                onBlurHandler={onBlurHandler}
                externalForceCount={externalForceCount}
                setExternalForceCount={setExternalForceCount}
                momentsCount={momentsCount}
                setMomentsCount={setMomentsCount}
              />
            </div>
            {/* Hide Excel Button */}
            {/* {excelDownloadLink ? (
              <div className="d-flex" style={{ justifyContent: "end" }}>
                <a
                  href={excelDownloadLink}
                  className="text-right p2 link cur"
                  style={{
                    padding: "10px",
                    paddingTop: "30px",
                    cursor: "pointer",
                  }}
                >
                  Download Excel{" "}
                  <i className="fa fa-download" aria-hidden="true"></i>
                </a>
              </div>
            ) : null} */}
          </form>
        </div>
      </div>
    </div>
  );
}

export default ProjectModal;
